import { useCallback, useEffect, useRef, useState } from "react"
import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useField } from "formik"
import { Chip, Divider, FormHelperText, Stack, Switch, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { useEnvironment } from "../../../../modules/environment/envHooks"
import {
    BookingMessageType,
    requestOutlookAttendees,
    subscribeToParentMessages,
} from "../../../../modules/order/pages/components/bookingMessaging"

import { useOutlookAttendeesOptIn } from "../OutlookAttendeesOptInContext"
import { AttendeeSource, isOutlookSourced, reconcileImportedAttendees } from "../outlookAttendees"
import { SwitchFieldRow } from "./form/SwitchFieldRow"

/** How long to wait for OUTLOOK_ATTENDEES_PROVIDE before giving up on an add-in that cannot answer. */
const ATTENDEES_REQUEST_TIMEOUT_MS = 8000

/** Long attendee lists are collapsed to this many chips so the dialog keeps its height. */
const VISIBLE_ATTENDEES_LIMIT = 8

const useStyles = makeStyles()((theme) => ({
    card: {
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[950],
        background: theme.palette.tailwind.slate[50],
        padding: "1rem 1.5rem 1.5rem",
    },
    chip: {
        borderRadius: "6.25rem",
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        color: theme.palette.tailwind.slate[700],
        "& .MuiChip-label": {
            fontSize: theme.typography.body2.fontSize,
        },
        "& .MuiChip-deleteIcon": {
            color: theme.palette.tailwind.slate[400],
            "&:hover": {
                color: theme.palette.tailwind.red[400],
            },
        },
    },
    expandChip: {
        borderRadius: "6.25rem",
        background: "transparent",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        color: theme.palette.tailwind.slate[500],
        "& .MuiChip-label": {
            fontSize: theme.typography.body2.fontSize,
            fontWeight: 500,
        },
    },
}))

type TaggedAttendee = Readonly<{
    name: string
    company: string
    isExternal: boolean
    source?: AttendeeSource
}>

type ShareOutlookAttendeesToggleProps = Readonly<{
    /** Formik field holding the participants list (REGISTER_PARTICIPANTS for service, ADD_PARTICIPANTS_FORM for booking). */
    fieldKey: string
    /** Read-only mode: the current list stays visible, but it cannot be imported, removed or turned off. */
    disabled?: boolean
}>

/**
 * Outlook-only opt-in: lets the user pull the Outlook meeting attendees into the order at the confirm step.
 * Rendered only for the Outlook web flow and only when the meeting actually has attendees (non-PII count).
 * Nothing is requested until the user turns it on — the attendees are delivered on demand via postMessage.
 * Once added, each imported participant is listed and can be removed individually.
 *
 * In edit mode, when the order was previously saved with Outlook attendees (`initialShare`), the toggle
 * starts on and eagerly reconciles the saved participants against the *current* Outlook meeting (auto-sync),
 * so re-opening never duplicates and the user can revert (turn off) the whole addition.
 */
export function ShareOutlookAttendeesToggle({ fieldKey, disabled = false }: ShareOutlookAttendeesToggleProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { currentEnv } = useEnvironment()
    const optIn = useOutlookAttendeesOptIn()
    const [{ value }, , helpers] = useField<TaggedAttendee[]>(fieldKey)

    const [isLoading, setIsLoading] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    // Everything Outlook-specific comes from the opt-in context (null outside the create-order Outlook flow),
    // so this component is safe to render inside the shared booking dialog even when used elsewhere.
    const share = optIn?.share ?? false
    const isOutlookWebFlow = optIn?.isOutlookWebFlow ?? false
    const count = optIn?.outlookAttendeeCount ?? 0
    const parentOrigin = new URLSearchParams(window.location.search).get("parentOrigin") || "*"

    const setFieldRef = useRef(helpers.setValue)
    setFieldRef.current = helpers.setValue
    const valueRef = useRef<TaggedAttendee[]>(value ?? [])
    valueRef.current = value ?? []
    const optInRef = useRef(optIn)
    optInRef.current = optIn
    // Names of the last-imported Outlook attendees (normalized). Used as a robust fallback for removal in
    // case the UI-only `source` tag is stripped by the service form's reinitialize/sync pipeline.
    const importedNamesRef = useRef<Set<string>>(new Set())
    const importTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const setShare = useCallback((next: boolean) => {
        optInRef.current?.setShare(next)
        if (optInRef.current) optInRef.current.shareRef.current = next
    }, [])

    const clearImportTimeout = useCallback(() => {
        if (importTimeoutRef.current === undefined) return
        clearTimeout(importTimeoutRef.current)
        importTimeoutRef.current = undefined
    }, [])

    // The headcount now also comes from an add-in that may be too old to answer the handshake, so a request
    // can go unanswered. Give up instead of leaving the switch on and stuck in its loading state.
    const armImportTimeout = useCallback(
        (revertShare: boolean) => {
            clearImportTimeout()
            importTimeoutRef.current = setTimeout(() => {
                importTimeoutRef.current = undefined
                setIsLoading(false)
                if (revertShare) setShare(false)
            }, ATTENDEES_REQUEST_TIMEOUT_MS)
        },
        [clearImportTimeout, setShare],
    )

    useEffect(() => clearImportTimeout, [clearImportTimeout])

    // An attendee counts as Outlook-imported if it carries the `source` tag OR its name matches a
    // just-imported one. The name fallback is needed because the service form's reinitialize/sync pipeline
    // strips the UI-only `source` tag, which would otherwise break removal and the imported list display.
    const isImported = useCallback(
        (attendee: TaggedAttendee) =>
            isOutlookSourced(attendee) || importedNamesRef.current.has(attendee.name.trim().toLowerCase()),
        [],
    )

    useEffect(() => {
        // Only register message handlers for the actual Outlook web flow with attendees — avoids adding
        // window/Office listeners (and postMessage attack surface) across every non-Outlook usage.
        const oi = optInRef.current
        if (!oi?.isOutlookWebFlow || oi.outlookAttendeeCount <= 0) return

        const unsubscribe = subscribeToParentMessages((msg) => {
            if (msg.type !== BookingMessageType.OUTLOOK_ATTENDEES_PROVIDE) return
            // Ignore late responses if the user turned the opt-in back off.
            if (!optInRef.current?.shareRef.current) return
            // Reconcile against the current Outlook meeting: dedupe by name (saved attendees carry no
            // provenance), drop stale tagged/placeholder rows, append the fresh tagged Outlook attendees.
            const reconciled = reconcileImportedAttendees(valueRef.current, msg.attendees)
            importedNamesRef.current = new Set(msg.attendees.map((a) => a.name.trim().toLowerCase()))
            setFieldRef.current(reconciled)
            clearImportTimeout()
            setIsLoading(false)
        })
        return unsubscribe
    }, [])

    useEffect(() => {
        // Edit mode: if this order was previously saved with Outlook attendees, eagerly pull the current
        // Outlook list once to reconcile (tag/refresh) the saved participants. They are already on the
        // backend, so this is not a new disclosure.
        if (!isOutlookWebFlow || count <= 0) return
        if (optInRef.current?.isEdit && optInRef.current?.share && !valueRef.current.some(isImported)) {
            setIsLoading(true)
            // Keep the saved opt-in on timeout: the participants are already on the order, and turning it
            // off here would silently revoke the choice recorded on the previous save.
            armImportTimeout(false)
            requestOutlookAttendees(parentOrigin, currentEnv).catch((e) => {
                console.error("Failed to request Outlook attendees", e)
                clearImportTimeout()
                setIsLoading(false)
            })
        }
    }, [])

    const handleToggle = useCallback(
        (next: boolean) => {
            setIsExpanded(false)
            setShare(next)
            if (next) {
                // Reset the remembered names before a fresh import so the auto-off effect doesn't fire
                // prematurely while the request is in flight.
                importedNamesRef.current = new Set()
                setIsLoading(true)
                armImportTimeout(true)
                requestOutlookAttendees(parentOrigin, currentEnv).catch((e) => {
                    console.error("Failed to request Outlook attendees", e)
                    clearImportTimeout()
                    setIsLoading(false)
                    setShare(false)
                })
            } else {
                // Remove Outlook-imported attendees (by tag or imported name); keep manual ones.
                // Keep `importedNamesRef` so the self-heal effect can catch any re-added (tag-stripped) ones.
                setFieldRef.current(valueRef.current.filter((a) => !isImported(a)))
            }
        },
        [parentOrigin, currentEnv, setShare, isImported, armImportTimeout, clearImportTimeout],
    )

    const handleRemove = useCallback(
        (index: number) => {
            // Remove by position, not by value: an Outlook meeting can legitimately carry two attendees with
            // the same display name, and matching on name+company would drop both at once. The index comes
            // from the current field value (not the object reference), which the service form reinitializes.
            const removed = valueRef.current[index]
            if (!removed) return

            const next = valueRef.current.filter((_, i) => i !== index)
            const name = removed.name.trim().toLowerCase()
            // Keep the name remembered while a namesake is still in the list — `isImported` relies on it.
            if (!next.some((a) => a.name.trim().toLowerCase() === name)) {
                importedNamesRef.current.delete(name)
            }
            setFieldRef.current(next)
            // When the last Outlook-imported participant is removed, flip the opt-in back off.
            if (!next.some(isImported)) {
                setShare(false)
            }
        },
        [setShare, isImported],
    )

    // The service form's reinitialize/sync can re-add just-removed imports from stale outer values. While the
    // opt-in is off, keep the field free of Outlook-imported attendees so removal actually sticks.
    useEffect(() => {
        if (share) return
        const current = value ?? []
        if (current.some(isImported)) {
            setFieldRef.current(current.filter((a) => !isImported(a)))
        }
    }, [share, value, isImported])

    // Turn the opt-in off automatically once the last Outlook-imported participant is gone — including
    // removals done in the "Edit participants" modal, which the toggle's own remove button doesn't cover.
    // Guarded by `importedNamesRef` so it doesn't fire while a fresh import request is still in flight.
    useEffect(() => {
        if (!share || importedNamesRef.current.size === 0) return
        if (!(value ?? []).some(isImported)) {
            setShare(false)
        }
    }, [share, value, isImported, setShare])

    if (!isOutlookWebFlow || count <= 0) return null

    // Keep each attendee's position in the field value: removal targets that index, so namesakes stay apart.
    const imported = (value ?? [])
        .map((attendee, index) => ({ attendee, index }))
        .filter(({ attendee }) => isImported(attendee))
    const showImported = share && imported.length > 0
    const hiddenCount = imported.length - VISIBLE_ATTENDEES_LIMIT
    const isCollapsible = hiddenCount > 0
    const visible = isCollapsible && !isExpanded ? imported.slice(0, VISIBLE_ATTENDEES_LIMIT) : imported

    return (
        <Stack gap=".75rem">
            <LocalizedStrict id="create-order-share-outlook-attendees-toggle-label" attrs={{ label: true }}>
                <SwitchFieldRow label="Add participants from Outlook">
                    <Switch
                        checked={share}
                        onChange={(e) => handleToggle(e.target.checked)}
                        disabled={isLoading || disabled}
                    />
                </SwitchFieldRow>
            </LocalizedStrict>

            {showImported ? (
                <Stack gap="1rem" className={classes.card}>
                    <Stack gap=".5rem">
                        <LocalizedStrict
                            id="create-order-share-outlook-attendees-list-title"
                            vars={{ count: imported.length.toString() }}
                        >
                            <Typography variant="subtitle1">Participants from Outlook ({imported.length}):</Typography>
                        </LocalizedStrict>
                        <Divider />
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap=".5rem">
                        {visible.map(({ attendee, index }) => (
                            <Chip
                                key={`${attendee.name}-${attendee.company}-${index}`}
                                className={classes.chip}
                                label={attendee.company ? `${attendee.name} (${attendee.company})` : attendee.name}
                                onDelete={disabled ? undefined : () => handleRemove(index)}
                                deleteIcon={
                                    <FontAwesomeIcon
                                        icon={faXmark}
                                        size="xs"
                                        title={l10n.getString("create-order-share-outlook-attendees-remove")}
                                    />
                                }
                            />
                        ))}
                        {isCollapsible && (
                            <Chip
                                className={classes.expandChip}
                                label={
                                    isExpanded
                                        ? l10n.getString("create-order-share-outlook-attendees-show-less-button")
                                        : l10n.getString("create-order-share-outlook-attendees-show-all-button", {
                                              count: hiddenCount,
                                          })
                                }
                                onClick={() => setIsExpanded((prev) => !prev)}
                            />
                        )}
                    </Stack>
                </Stack>
            ) : (
                <LocalizedStrict
                    id="create-order-share-outlook-attendees-toggle-helper"
                    vars={{ count: count.toString() }}
                >
                    <FormHelperText>
                        Turning this on adds {count} participant(s) from your Outlook meeting.
                    </FormHelperText>
                </LocalizedStrict>
            )}
        </Stack>
    )
}
